// gioco/voce.ts — v026: voci buffe. Il microfono passa da un piccolo «laboratorio» Web Audio (robot, scoiattolo,
// gigante, eco, alieno, radio) e all'altro arriva la voce cambiata. Solo nodi standard (niente AudioWorklet: va con
// la stessa politica di sicurezza del sito e su Safari). Con «normale» si manda il microfono vero, senza ritardo.
// Il cambio di altezza (scoiattolo, gigante) usa il metodo «Jungle» di Chris Wilson: due linee di ritardo modulate
// a dente di sega, con dissolvenza incrociata.
// v046: più voci da attore (elio, orco, da uomo a donna e viceversa, papera, nonnina, fantasma, sott'acqua), in
// ordine «prima le più divertenti», e un limitatore in uscita su tutte le voci cambiate (niente botte né gracchi).

/** Le voci, nell'ordine della griglia: «normale» per tornare indietro, poi le più divertenti. */
export const VOCI = [
  "normale",
  "elio",
  "orco",
  "donna",
  "uomo",
  "papera",
  "scoiattolo",
  "gigante",
  "robot",
  "alieno",
  "nonnina",
  "fantasma",
  "sottacqua",
  "eco",
  "radio",
] as const;
export type Voce = (typeof VOCI)[number];

export function eVoce(v: unknown): v is Voce {
  return typeof v === "string" && (VOCI as readonly string[]).includes(v);
}

/** v046: le voci «sicure e simpatiche» per partire già buffi: si capisce bene cosa dici e non danno fastidio. */
export const VOCI_INIZIALI: readonly Voce[] = ["scoiattolo", "gigante", "donna", "uomo", "alieno", "nonnina"];

/** v046: una voce iniziale a caso tra `VOCI_INIZIALI` (`caso` da 0 a 1, di solito Math.random). */
export function voceIniziale(caso: () => number = Math.random): Voce {
  const i = Math.min(VOCI_INIZIALI.length - 1, Math.max(0, Math.floor(caso() * VOCI_INIZIALI.length)));
  return VOCI_INIZIALI[i]!;
}

type Ctx = AudioContext;

/**
 * Ritardo modulato per cambiare l'altezza della voce. `spostamento` da -1.6 (più grave) a 2 (più acuta): il rapporto
 * di frequenza è circa 1 + spostamento/2 (0.6 ≈ +4.5 semitoni, -0.46 ≈ -4.5, 1.8 ≈ +11, -0.9 ≈ -10).
 */
function altezza(ctx: Ctx, spostamento: number): { ingresso: AudioNode; uscita: AudioNode; ferma: () => void } {
  const RITARDO = 0.1;
  const DISSOLVENZA = 0.05;
  const BUFFER = 0.1;
  const sr = ctx.sampleRate;
  const bufferDissolvenza = (() => {
    const l1 = Math.round(BUFFER * sr);
    const l2 = Math.round((BUFFER - 2 * DISSOLVENZA) * sr);
    const b = ctx.createBuffer(1, l1 + l2, sr);
    const p = b.getChannelData(0);
    const lf = DISSOLVENZA * sr;
    const i2 = l1 - lf;
    for (let i = 0; i < l1; i++) p[i] = i < lf ? Math.sqrt(i / lf) : i >= i2 ? Math.sqrt(1 - (i - i2) / lf) : 1;
    return b;
  })();
  const bufferRitardo = (su: boolean) => {
    const l1 = Math.round(BUFFER * sr);
    const l2 = Math.round((BUFFER - 2 * DISSOLVENZA) * sr);
    const lunghezza = l1 + l2;
    const b = ctx.createBuffer(1, lunghezza, sr);
    const p = b.getChannelData(0);
    for (let i = 0; i < l1; i++) p[i] = su ? (l1 - i) / lunghezza : i / l1;
    return b;
  };
  const ingresso = ctx.createGain();
  const uscita = ctx.createGain();
  const su = spostamento > 0;
  const fonte = (b: AudioBuffer) => {
    const s = ctx.createBufferSource();
    s.buffer = b;
    s.loop = true;
    return s;
  };
  const mod1 = fonte(bufferRitardo(su));
  const mod2 = fonte(bufferRitardo(su));
  const modGain1 = ctx.createGain();
  const modGain2 = ctx.createGain();
  const r1 = ctx.createDelay(1);
  const r2 = ctx.createDelay(1);
  mod1.connect(modGain1);
  mod2.connect(modGain2);
  modGain1.connect(r1.delayTime);
  modGain2.connect(r2.delayTime);
  const f1 = fonte(bufferDissolvenza);
  const f2 = fonte(bufferDissolvenza);
  const mix1 = ctx.createGain();
  const mix2 = ctx.createGain();
  mix1.gain.value = 0;
  mix2.gain.value = 0;
  f1.connect(mix1.gain);
  f2.connect(mix2.gain);
  ingresso.connect(r1);
  ingresso.connect(r2);
  r1.connect(mix1);
  r2.connect(mix2);
  mix1.connect(uscita);
  mix2.connect(uscita);
  const ritardo = RITARDO * Math.min(su ? 2 : 1.6, Math.abs(spostamento)); // v046: fino a 2 (elio)
  modGain1.gain.value = 0.5 * ritardo;
  modGain2.gain.value = 0.5 * ritardo;
  const t = ctx.currentTime + 0.05;
  const t2 = t + BUFFER - DISSOLVENZA;
  mod1.start(t);
  mod2.start(t2);
  f1.start(t);
  f2.start(t2);
  return {
    ingresso,
    uscita,
    ferma: () => {
      for (const s of [mod1, mod2, f1, f2]) {
        try {
          s.stop();
        } catch {
          // già fermo
        }
      }
    },
  };
}

/** Eco: la voce torna indietro qualche volta, più piano. */
function eco(ctx: Ctx, tempo: number, ritorno: number): { ingresso: AudioNode; uscita: AudioNode } {
  const ingresso = ctx.createGain();
  const uscita = ctx.createGain();
  const r = ctx.createDelay(2);
  r.delayTime.value = tempo;
  const fb = ctx.createGain();
  fb.gain.value = ritorno;
  ingresso.connect(uscita);
  ingresso.connect(r);
  r.connect(fb);
  fb.connect(r);
  r.connect(uscita);
  return { ingresso, uscita };
}

/** Distorsione morbida (radio, robot). */
function distorsione(ctx: Ctx, quanto: number): WaveShaperNode {
  const n = 1024;
  const curva = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curva[i] = ((3 + quanto) * x * 20 * (Math.PI / 180)) / (Math.PI + quanto * Math.abs(x));
  }
  const w = ctx.createWaveShaper();
  w.curve = curva;
  w.oversample = "2x";
  return w;
}

/**
 * Il laboratorio della voce per un microfono. `traccia(voce)` restituisce la traccia audio da mandare:
 * per «normale» quella del microfono, per le altre quella elaborata.
 */
export class LaboratorioVoce {
  private ctx: AudioContext | null = null;
  private sorgente: MediaStreamAudioSourceNode | null = null;
  private destinazione: MediaStreamAudioDestinationNode | null = null;
  private pulizie: (() => void)[] = [];
  private nodi: AudioNode[] = [];
  private attuale: Voce = "normale";

  private readonly microfono: MediaStreamTrack;

  constructor(microfono: MediaStreamTrack) {
    this.microfono = microfono;
  }

  /** La voce scelta adesso. */
  get voce(): Voce {
    return this.attuale;
  }

  /** v046: l'audio del browser è ancora fermo (serve un tocco): intanto va mandato il microfono vero. */
  sospeso(): boolean {
    return this.ctx !== null && this.ctx.state !== "running";
  }
  /** v046: prova ad accendere l'audio (dentro un tocco va sempre; prima, dipende dal browser). */
  async riprendi(): Promise<boolean> {
    if (!this.ctx) return true;
    await this.ctx.resume().catch(() => undefined);
    return this.ctx.state === "running";
  }

  /** Cambia voce e restituisce la traccia da mandare all'altro. */
  traccia(voce: Voce): MediaStreamTrack {
    this.attuale = voce;
    this.smonta();
    if (voce === "normale") return this.microfono;
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return this.microfono;
    if (!this.ctx) {
      this.ctx = new C();
      this.sorgente = this.ctx.createMediaStreamSource(new MediaStream([this.microfono]));
      this.destinazione = this.ctx.createMediaStreamDestination();
    }
    void this.ctx.resume().catch(() => undefined);
    const ctx = this.ctx;
    const ingresso = ctx.createGain();
    const finale = ctx.createGain();
    // v046: limitatore in uscita, uguale per tutte le voci (attacco rapido: le voci con risonanze non gracchiano)
    const limite = ctx.createDynamicsCompressor();
    limite.threshold.value = -8;
    limite.knee.value = 6;
    limite.ratio.value = 12;
    limite.attack.value = 0.003;
    limite.release.value = 0.15;
    this.nodi.push(ingresso, finale, limite);
    this.sorgente!.connect(ingresso);
    finale.connect(limite);
    limite.connect(this.destinazione!);
    this.costruisci(voce, ctx, ingresso, finale);
    // la traccia segue il microfono: se il microfono è spento, anche la voce cambiata tace
    const out = this.destinazione!.stream.getAudioTracks()[0]!;
    out.enabled = this.microfono.enabled;
    return out;
  }

  private costruisci(voce: Voce, ctx: Ctx, ingresso: GainNode, finale: GainNode): void {
    const tieni = (...n: AudioNode[]) => this.nodi.push(...n);
    /** v046: un oscillatore lento che muove `param` di ±`quanto` (vibrato, tremolo, filtri che ondeggiano). */
    const oscilla = (param: AudioParam, hz: number, quanto: number) => {
      const o = ctx.createOscillator();
      o.frequency.value = hz;
      const g = ctx.createGain();
      g.gain.value = quanto;
      o.connect(g);
      g.connect(param);
      o.start();
      this.pulizie.push(() => o.stop());
      tieni(o, g);
    };
    /** v046: un filtro biquad già regolato. */
    const filtro = (tipo: BiquadFilterType, hz: number, q = 0.707, guadagnoDb = 0) => {
      const f = ctx.createBiquadFilter();
      f.type = tipo;
      f.frequency.value = hz;
      f.Q.value = q;
      f.gain.value = guadagnoDb;
      tieni(f);
      return f;
    };
    /** v046: cambio di altezza già montato e pulito alla fine. */
    const alza = (spostamento: number) => {
      const a = altezza(ctx, spostamento);
      this.pulizie.push(a.ferma);
      tieni(a.ingresso, a.uscita);
      return a;
    };
    /** v046: vibrato (la voce «trema» di altezza): ritardo corto mosso da un oscillatore. */
    const vibrato = (hz: number, profondita: number) => {
      const r = ctx.createDelay(0.05);
      r.delayTime.value = profondita + 0.002;
      tieni(r);
      oscilla(r.delayTime, hz, profondita);
      return r;
    };
    /** v046: tremolo (la voce «trema» di volume) da 0 a 1 di profondità. */
    const tremolo = (hz: number, profondita: number) => {
      const g = ctx.createGain();
      g.gain.value = 1 - profondita;
      tieni(g);
      oscilla(g.gain, hz, profondita);
      return g;
    };
    /** v046: distorsione morbida già tenuta per la pulizia. */
    const sporca = (quanto: number) => {
      const d = distorsione(ctx, quanto);
      tieni(d);
      return d;
    };
    /** v046: collega in fila i nodi e restituisce l'ultimo. */
    const catena = (...n: AudioNode[]) => {
      for (let i = 0; i < n.length - 1; i++) n[i]!.connect(n[i + 1]!);
      return n[n.length - 1]!;
    };
    switch (voce) {
      case "elio": {
        // palloncino di elio: altissima e sottile (circa +11 semitoni, niente bassi)
        const a = alza(1.8);
        catena(ingresso, a.ingresso);
        catena(a.uscita, filtro("highpass", 350), filtro("peaking", 3000, 1, 4), finale);
        finale.gain.value = 1.2;
        break;
      }
      case "orco": {
        // profondissima e ruvida: -10 semitoni, bassi in su, un ringhio veloce e un filo di distorsione
        const a = alza(-0.9);
        catena(ingresso, a.ingresso);
        catena(a.uscita, filtro("lowshelf", 180, 0.707, 7), tremolo(31, 0.25), sporca(10), filtro("lowpass", 3200), finale);
        finale.gain.value = 1.1;
        break;
      }
      case "donna": {
        // da uomo a donna: +4.5 semitoni, meno petto, più brillante
        const a = alza(0.6);
        catena(ingresso, a.ingresso);
        catena(a.uscita, filtro("highpass", 170), filtro("peaking", 300, 1, -3), filtro("highshelf", 3000, 0.707, 4), finale);
        finale.gain.value = 1.1;
        break;
      }
      case "uomo": {
        // da donna a uomo: -4.5 semitoni, più petto, più cupa
        const a = alza(-0.46);
        catena(ingresso, a.ingresso);
        catena(a.uscita, filtro("lowshelf", 200, 0.707, 5), filtro("highshelf", 3500, 0.707, -5), finale);
        finale.gain.value = 1.2;
        break;
      }
      case "papera": {
        // nasale e fastidiosa: un po' più acuta, banda stretta intorno a 1.4 kHz e un po' di raschio
        const a = alza(0.45);
        catena(ingresso, a.ingresso);
        catena(a.uscita, filtro("highpass", 650), filtro("peaking", 1400, 3, 12), filtro("lowpass", 3400), sporca(20), finale);
        finale.gain.value = 0.8;
        break;
      }
      case "nonnina": {
        // voce tremolante: vibrato + tremolo, un po' più acuta e sottile
        const a = alza(0.25);
        catena(ingresso, filtro("highpass", 230), vibrato(5.5, 0.0018), a.ingresso);
        catena(a.uscita, tremolo(7, 0.3), filtro("lowpass", 3800), finale);
        finale.gain.value = 1.3;
        break;
      }
      case "fantasma": {
        // un po' più acuta, ondeggia piano, riverbero da castello (quattro pettini) + eco lunga
        const a = alza(0.3);
        catena(ingresso, vibrato(0.6, 0.004), a.ingresso);
        const coda = ctx.createGain();
        coda.gain.value = 0.22;
        tieni(coda);
        for (const tempo of [0.0297, 0.0371, 0.0411, 0.0437]) {
          const r = ctx.createDelay(0.1);
          r.delayTime.value = tempo;
          const fb = ctx.createGain();
          fb.gain.value = 0.72;
          const scuro = filtro("lowpass", 2500);
          tieni(r, fb);
          a.uscita.connect(r);
          catena(r, scuro, fb, r);
          scuro.connect(coda);
        }
        const e = eco(ctx, 0.33, 0.35);
        tieni(e.ingresso, e.uscita);
        const asciutta = ctx.createGain();
        asciutta.gain.value = 0.6;
        tieni(asciutta);
        a.uscita.connect(asciutta);
        asciutta.connect(e.ingresso);
        coda.connect(e.ingresso);
        e.uscita.connect(finale);
        finale.gain.value = 0.9;
        break;
      }
      case "sottacqua": {
        // glu glu: passa-basso risonante che ondeggia + vibrato forte
        const f = filtro("lowpass", 750, 5);
        oscilla(f.frequency, 0.9, 380);
        const prima = ctx.createGain();
        prima.gain.value = 0.55;
        tieni(prima);
        catena(ingresso, prima, vibrato(4.5, 0.0028), f, finale);
        finale.gain.value = 1.4;
        break;
      }
      case "scoiattolo":
      case "gigante": {
        const a = altezza(ctx, voce === "scoiattolo" ? 0.85 : -0.6);
        this.pulizie.push(a.ferma);
        tieni(a.ingresso, a.uscita);
        ingresso.connect(a.ingresso);
        a.uscita.connect(finale);
        finale.gain.value = voce === "gigante" ? 1.3 : 1;
        break;
      }
      case "robot": {
        // modulazione ad anello + un filo di ritardo metallico
        const anello = ctx.createGain();
        anello.gain.value = 0;
        const osc = ctx.createOscillator();
        osc.type = "square";
        osc.frequency.value = 55;
        osc.connect(anello.gain);
        osc.start();
        this.pulizie.push(() => osc.stop());
        const metallo = eco(ctx, 0.012, 0.55);
        const d = distorsione(ctx, 30);
        tieni(anello, osc, metallo.ingresso, metallo.uscita, d);
        ingresso.connect(anello);
        anello.connect(metallo.ingresso);
        metallo.uscita.connect(d);
        d.connect(finale);
        finale.gain.value = 1.6;
        break;
      }
      case "eco": {
        const e = eco(ctx, 0.28, 0.45);
        tieni(e.ingresso, e.uscita);
        ingresso.connect(e.ingresso);
        e.uscita.connect(finale);
        finale.gain.value = 0.8;
        break;
      }
      case "alieno": {
        // vibrato veloce + un poco più acuta
        const r = ctx.createDelay(0.05);
        r.delayTime.value = 0.006;
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 7;
        const prof = ctx.createGain();
        prof.gain.value = 0.004;
        lfo.connect(prof);
        prof.connect(r.delayTime);
        lfo.start();
        this.pulizie.push(() => lfo.stop());
        const a = altezza(ctx, 0.35);
        this.pulizie.push(a.ferma);
        tieni(r, lfo, prof, a.ingresso, a.uscita);
        ingresso.connect(r);
        r.connect(a.ingresso);
        a.uscita.connect(finale);
        break;
      }
      case "radio": {
        const alto = ctx.createBiquadFilter();
        alto.type = "highpass";
        alto.frequency.value = 600;
        const basso = ctx.createBiquadFilter();
        basso.type = "lowpass";
        basso.frequency.value = 2800;
        const d = distorsione(ctx, 60);
        tieni(alto, basso, d);
        ingresso.connect(alto);
        alto.connect(basso);
        basso.connect(d);
        d.connect(finale);
        finale.gain.value = 1.4;
        break;
      }
      default:
        ingresso.connect(finale);
    }
  }

  private smonta(): void {
    this.sorgente?.disconnect();
    for (const f of this.pulizie.splice(0)) {
      try {
        f();
      } catch {
        // già fermo
      }
    }
    for (const n of this.nodi.splice(0)) n.disconnect();
  }

  chiudi(): void {
    this.smonta();
    this.sorgente?.disconnect();
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }
}
